import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { 
  Plus, Search, Filter, Package, AlertTriangle, 
  TrendingUp, TrendingDown, BarChart3, QrCode,
  Eye, Edit, MoreVertical, Download
} from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Select from '../components/ui/Select'
import Modal from '../components/Modal'
import Pagination from '../components/ui/Pagination'

import produtosService, { 
  ProdutoFilters,
  formatarValor,
  getStatusEstoque,
  formatarQuantidade
} from '../services/produtos'
import { Produto } from '../types'
import ProdutoForm from '../components/produtos/ProdutoForm'
import ProdutoDetails from '../components/produtos/ProdutoDetails'
import MovimentacaoForm from '../components/produtos/MovimentacaoForm'
import QRCodeScanner from '../components/produtos/QRCodeScanner'

const EstoquePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()

  // Estados locais
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showMovimentacaoModal, setShowMovimentacaoModal] = useState(false)
  const [showScannerModal, setShowScannerModal] = useState(false)
  const [selectedProduto, setSelectedProduto] = useState<Produto | null>(null)
  const [showFilters, setShowFilters] = useState(false)

  // Filtros
  const [filters, setFilters] = useState<ProdutoFilters>({
    page: parseInt(searchParams.get('page') || '1'),
    limit: parseInt(searchParams.get('limit') || '25'),
    search: searchParams.get('search') || '',
    categoria: searchParams.get('categoria') || undefined,
    ativo: searchParams.get('ativo') ? searchParams.get('ativo') === 'true' : undefined,
    estoqueZerado: searchParams.get('estoqueZerado') === 'true' || undefined,
    estoqueMinimo: searchParams.get('estoqueMinimo') === 'true' || undefined
  })

  // Queries
  const { data: produtosData, isLoading } = useQuery({
    queryKey: ['produtos', filters],
    queryFn: () => produtosService.list(filters)
  })

  const { data: categorias } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => produtosService.getCategorias()
  })

  const { data: stats } = useQuery({
    queryKey: ['produtos-stats'],
    queryFn: () => produtosService.getStats()
  })

  // Efeitos
  useEffect(() => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        params.set(key, String(value))
      }
    })
    setSearchParams(params)
  }, [filters, setSearchParams])

  // Handlers
  const handleSearch = (search: string) => {
    setFilters(prev => ({ ...prev, search, page: 1 }))
  }

  const handleFilterChange = (key: keyof ProdutoFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }))
  }

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }

  const handleCreate = () => {
    setShowCreateModal(true)
  }

  const handleEdit = (produto: Produto) => {
    setSelectedProduto(produto)
    setShowEditModal(true)
  }

  const handleView = (produto: Produto) => {
    setSelectedProduto(produto)
    setShowDetailsModal(true)
  }

  const handleMovimentacao = (produto: Produto) => {
    setSelectedProduto(produto)
    setShowMovimentacaoModal(true)
  }

  const handleFormSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['produtos'] })
    queryClient.invalidateQueries({ queryKey: ['produtos-stats'] })
    setShowCreateModal(false)
    setShowEditModal(false)
    setShowMovimentacaoModal(false)
    setSelectedProduto(null)
  }

  const handleScanSuccess = (produto: Produto) => {
    setSelectedProduto(produto)
    setShowScannerModal(false)
    setShowDetailsModal(true)
  }

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 25,
      search: '',
      categoria: undefined,
      ativo: undefined,
      estoqueZerado: undefined,
      estoqueMinimo: undefined
    })
  }

  const exportarProdutos = async (format: 'csv' | 'xlsx' | 'pdf') => {
    try {
      await produtosService.exportar(format, filters)
    } catch (error) {
      console.error('Erro ao exportar produtos:', error)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header com estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Package className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Total de Produtos</h3>
              <p className="text-2xl font-bold text-blue-600">{stats?.total || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Stock Baixo</h3>
              <p className="text-2xl font-bold text-yellow-600">{stats?.stockMinimo || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingDown className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Sem Stock</h3>
              <p className="text-2xl font-bold text-red-600">{stats?.stockZerado || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Valor Total</h3>
              <p className="text-2xl font-bold text-green-600">{formatarValor(stats?.valorTotalStock || 0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Header principal */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestão de Stock</h1>
          <p className="text-gray-600">
            {produtosData?.pagination?.totalItems || 0} produtos em stock
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowScannerModal(true)}
            leftIcon={<QrCode className="h-4 w-4" />}
          >
            Scanner QR
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            leftIcon={<Filter className="h-4 w-4" />}
          >
            Filtros
          </Button>
          <div className="relative">
            <Button
              variant="outline"
              leftIcon={<Download className="h-4 w-4" />}
            >
              Exportar
            </Button>
            {/* Dropdown seria implementado aqui */}
          </div>
          <Button
            onClick={handleCreate}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Novo Produto
          </Button>
        </div>
      </div>

      {/* Barra de pesquisa */}
      <div className="flex gap-4">
        <div className="flex-1">
          <Input
            placeholder="Buscar por nome, SKU, código de barras..."
            value={filters.search}
            onChange={(e) => handleSearch(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
      </div>

      {/* Filtros avançados */}
      {showFilters && (
        <div className="bg-gray-50 p-4 rounded-lg grid grid-cols-1 md:grid-cols-5 gap-4">
          <Select
            label="Categoria"
            value={filters.categoriaId?.toString() || ''}
            onChange={(e) => handleFilterChange('categoriaId', e.target.value ? parseInt(e.target.value) : undefined)}
            options={[
              { value: '', label: 'Todas as categorias' },
              ...(categorias?.map(categoria => ({
                value: categoria.id.toString(),
                label: categoria.nome
              })) || [])
            ]}
          />
          
          <Select
            label="Status"
            value={filters.ativo !== undefined ? String(filters.ativo) : ''}
            onChange={(e) => handleFilterChange('ativo', e.target.value === '' ? undefined : e.target.value === 'true')}
            options={[
              { value: '', label: 'Todos' },
              { value: 'true', label: 'Ativos' },
              { value: 'false', label: 'Inativos' }
            ]}
          />

          <Select
            label="Stock"
            value={
              filters.estoqueZerado ? 'zerado' :
              filters.estoqueMinimo ? 'baixo' : ''
            }
            onChange={(e) => {
              const value = e.target.value
              if (value === 'zerado') {
                handleFilterChange('estoqueZerado', true)
                handleFilterChange('estoqueMinimo', undefined)
              } else if (value === 'baixo') {
                handleFilterChange('estoqueMinimo', true)
                handleFilterChange('estoqueZerado', undefined)
              } else {
                handleFilterChange('estoqueZerado', undefined)
                handleFilterChange('estoqueMinimo', undefined)
              }
            }}
            options={[
              { value: '', label: 'Todos' },
              { value: 'zerado', label: 'Sem stock' },
              { value: 'baixo', label: 'Stock baixo' }
            ]}
          />

          <div className="md:col-span-2 flex justify-end">
            <Button
              variant="outline"
              onClick={clearFilters}
            >
              Limpar Filtros
            </Button>
          </div>
        </div>
      )}

      {/* Lista de produtos */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Carregando produtos...</p>
          </div>
        ) : produtosData?.produtos?.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Nenhum produto encontrado</h3>
            <p className="text-gray-600">
              {Object.keys(filters).some(key => filters[key as keyof ProdutoFilters] && key !== 'page' && key !== 'limit')
                ? 'Tente ajustar os filtros ou'
                : 'Comece por'
              } cadastrar um novo produto.
            </p>
            <Button
              className="mt-4"
              onClick={handleCreate}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              Novo Produto
            </Button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Produto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Categoria
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      SKU
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stock
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Preço
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {produtosData?.produtos?.map((produto) => {
                    const statusEstoque = getStatusEstoque(produto)
                    
                    return (
                      <tr key={produto.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {produto.nome}
                            </div>
                            {produto.marca && produto.modelo && (
                              <div className="text-sm text-gray-500">
                                {produto.marca} {produto.modelo}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{produto.categoria?.nome || '-'}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-mono text-gray-900">{produto.codigoInterno}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${
                              statusEstoque.color === 'red' ? 'text-red-600' :
                              statusEstoque.color === 'yellow' ? 'text-yellow-600' :
                              'text-green-600'
                            }`}>
                              {formatarQuantidade(produto.stockAtual)}
                            </span>
                            <span className="text-xs text-gray-500">
                              (min: {formatarQuantidade(produto.stockMinimo)})
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatarValor(produto.precoVenda)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full ${
                            statusEstoque.color === 'red' ? 'bg-red-100 text-red-800' :
                            statusEstoque.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {statusEstoque.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(produto)}
                              title="Ver detalhes"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(produto)}
                              title="Editar"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMovimentacao(produto)}
                              title="Movimentar stock"
                            >
                              <BarChart3 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {produtosData && produtosData.pagination && (
              <Pagination
                pagination={produtosData.pagination}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>

      {/* Modais */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Novo Produto"
        size="xl"
      >
        <ProdutoForm
          onSuccess={handleFormSuccess}
          onCancel={() => setShowCreateModal(false)}
        />
      </Modal>

      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Editar Produto"
        size="xl"
      >
        {selectedProduto && (
          <ProdutoForm
            produto={selectedProduto}
            onSuccess={handleFormSuccess}
            onCancel={() => setShowEditModal(false)}
          />
        )}
      </Modal>

      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Detalhes do Produto"
        size="xl"
      >
        {selectedProduto && (
          <ProdutoDetails
            produto={selectedProduto}
            onEdit={() => {
              setShowDetailsModal(false)
              handleEdit(selectedProduto)
            }}
            onMovimentar={() => {
              setShowDetailsModal(false)
              handleMovimentacao(selectedProduto)
            }}
            onClose={() => setShowDetailsModal(false)}
          />
        )}
      </Modal>

      <Modal
        isOpen={showMovimentacaoModal}
        onClose={() => setShowMovimentacaoModal(false)}
        title="Movimentar Stock"
        size="md"
      >
        {selectedProduto && (
          <MovimentacaoForm
            produto={selectedProduto}
            onSuccess={handleFormSuccess}
            onCancel={() => setShowMovimentacaoModal(false)}
          />
        )}
      </Modal>

      <Modal
        isOpen={showScannerModal}
        onClose={() => setShowScannerModal(false)}
        title="Scanner QR Code"
        size="md"
      >
        <QRCodeScanner
          onSuccess={handleScanSuccess}
          onCancel={() => setShowScannerModal(false)}
        />
      </Modal>
    </div>
  )
}

export default EstoquePage